/**
 * Choose the production this batch lands on, or create one inline.
 *
 * Creating from here matters: ops routinely get a manifest for a show that
 * isn't in the system yet, and bouncing to another screen loses the paste.
 */

import { Plus } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { BulkAssignCompany, BulkAssignGroup } from './types';

interface GroupPickerProps {
  parentGroups: BulkAssignGroup[];
  companies: BulkAssignCompany[];
  companyNameFor: (companyId: string) => string;
  value: string;
  onChange: (parentGroupId: string) => void;
  /** True while a production still needs picking — highlights the row. */
  needsChoice: boolean;
  creating: boolean;
  onStartCreate: () => void;
  onCancelCreate: () => void;
  newName: string;
  onNewName: (name: string) => void;
  newCompanyId: string;
  onNewCompanyId: (companyId: string) => void;
  onCreate: () => void;
  isCreating: boolean;
  groupLabel: string;
}

export function GroupPicker({
  parentGroups,
  companies,
  companyNameFor,
  value,
  onChange,
  needsChoice,
  creating,
  onStartCreate,
  onCancelCreate,
  newName,
  onNewName,
  newCompanyId,
  onNewCompanyId,
  onCreate,
  isCreating,
  groupLabel,
}: GroupPickerProps) {
  if (creating) {
    return (
      <div className="flex flex-col gap-2.5 rounded-md border border-[#8FB560]/35 bg-[#8FB560]/[0.06] p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-[#8FB560]">
            New {groupLabel.toLowerCase()}
          </span>
          <button
            type="button"
            onClick={onCancelCreate}
            className="-mr-2 h-[26px] rounded-md px-2 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            Pick an existing one
          </button>
        </div>

        <input
          value={newName}
          onChange={(e) => onNewName(e.target.value)}
          placeholder={`${groupLabel} name`}
          className="h-[34px] w-full rounded-md border border-border bg-background px-2.5 text-[13px] text-foreground outline-none"
        />

        <div className="flex gap-2">
          <Select value={newCompanyId} onValueChange={onNewCompanyId}>
            <SelectTrigger className="h-[34px] min-w-0 flex-1 bg-background text-[13px]">
              <SelectValue placeholder="Company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={onCreate}
            disabled={!newName.trim() || isCreating}
            className="h-[34px] flex-none rounded-md px-3.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:bg-accent disabled:text-muted-foreground/80 bg-[#8FB560] text-black hover:brightness-95"
          >
            {isCreating ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`-m-2 flex items-center gap-2 rounded-lg p-2 transition-[background-color,border-color] ${
        needsChoice ? 'border border-[#8FB560]/45 bg-[#8FB560]/[0.07]' : 'border border-transparent'
      }`}
    >
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          className={`h-[38px] min-w-0 flex-1 ${
            needsChoice ? 'border-[#8FB560] bg-[#8FB560]/10' : 'bg-background'
          }`}
        >
          <SelectValue placeholder={`Select ${groupLabel.toLowerCase()}…`} />
        </SelectTrigger>
        <SelectContent>
          {parentGroups.map((group) => (
            <SelectItem key={group.id} value={group.id}>
              {group.name} · {companyNameFor(group.companyId)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <button
        type="button"
        onClick={onStartCreate}
        className={`flex h-[38px] flex-none items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-[13px] font-medium text-[#8FB560] transition-[background-color,border-color] hover:bg-accent hover:border-[#8FB560] ${
          needsChoice ? 'border border-[#8FB560] bg-[#8FB560]/[0.12]' : 'border border-border bg-background'
        }`}
      >
        <Plus className="h-3.5 w-3.5 flex-none" />
        Create New
      </button>
    </div>
  );
}
